import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import type { Asset } from '../../../types/asset.types';
import TrashCard from './TrashCard';
import { TRASH_RETENTION_DAYS } from './trashRetention';

type TrashGridProps = {
  assets: Asset[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRestore: (asset: Asset) => void;
  onPurge: (asset: Asset) => void;
  busyAssetUUID?: string | null;
};

/**
 * Trash tab content. Presentational: the page owns the listing query so the
 * tab's count badge and this grid share a single cache subscription.
 */
const TrashGrid = ({
  assets,
  isLoading,
  isError,
  onRetry,
  onRestore,
  onPurge,
  busyAssetUUID = null
}: TrashGridProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={Trash2}
        title="Couldn't Load the Trash"
        description="Something went wrong fetching deleted photos."
        action={
          <Button type="button" variant="outline" onClick={onRetry}>
            Try Again
          </Button>
        }
      />
    );
  }

  if (assets.length === 0) {
    return (
      <EmptyState
        icon={Trash2}
        title="Trash Is Empty"
        description={`Deleted photos land here and stay recoverable for ${TRASH_RETENTION_DAYS} days before they're permanently removed.`}
      />
    );
  }

  return (
    <>
      <Typography as="p" variant="body" className="mb-4 text-muted-foreground">
        Deleted photos are kept here for {TRASH_RETENTION_DAYS} days, then
        permanently removed. Restore a photo to send it back to the end of the
        photo set.
      </Typography>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(266px,1fr))] gap-3">
        {assets.map((asset) => (
          <TrashCard
            key={asset.assetUUID}
            asset={asset}
            onRestore={onRestore}
            onPurge={onPurge}
            disabled={busyAssetUUID === asset.assetUUID}
          />
        ))}
      </div>
    </>
  );
};

export default TrashGrid;
